/**
 * Owner Reservations Mapper
 * 
 * Maps raw reservation API responses to canonical UI objects used by both
 * ReservationsPage and CalendarPage. Ensures consistent type coercion and
 * date parsing across pages.
 * 
 * Phase 2: Computes financial snapshots for ALL statuses (not just BOOKED)
 * and adds rollup eligibility gates.
 */

import { getStatusBucket, isInquiryStatus, isCancelledStatus } from './hostawayStatus.js';

/**
 * Parse decimal string to number (safe, returns null if invalid)
 * @param {string|number|null|undefined} value - Value to parse
 * @returns {number|null} - Parsed number or null
 */
function parseDecimal(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  if (typeof value === 'number') {
    return isNaN(value) ? null : value;
  }
  const parsed = parseFloat(value);
  return isNaN(parsed) ? null : parsed;
}

/**
 * Compute financial snapshot for a reservation (ALL statuses).
 * Owner Net = Owner Revenue (post-deductions): single source of truth from canonical_financial_summary.owner_revenue.amount.
 * When canonical owner_revenue is missing, ownerNet is null (display N/A); no UI-side recomputation to avoid drift.
 * @param {Object} apiReservation - Raw reservation from API
 * @returns {Object} - Financial snapshot with ownerNet, fees, etc.
 */
function computeFinancialSnapshot(apiReservation) {
  const canonical = apiReservation.canonical_financial_summary;
  // Owner Revenue = post-deductions; only use canonical (matches booking detail and backend aggregates)
  const canonicalOwnerRevenue = canonical?.owner_revenue?.amount;
  const canonicalOwnerRevenueNum = canonicalOwnerRevenue != null ? parseDecimal(canonicalOwnerRevenue) : null;

  const channel = (apiReservation.channel || '').toLowerCase();
  const snapshot = {
    ownerNet: null,
    processingFees: null,
    channelFees: null,
    taxes: null,
    grossGuestPaid: null,
    ownerNetReason: null,
    processingFeesReason: null,
    channelFeesReason: null,
    taxesReason: null,
    grossGuestPaidReason: null,
  };

  // Channel-aware mapping
  if (channel === 'airbnb') {
    // Airbnb: ownerNet = AirbnbPayoutSum (airbnb_expected_payout_amount)
    snapshot.ownerNet = parseDecimal(apiReservation.airbnb_expected_payout_amount);
    snapshot.ownerNetReason = snapshot.ownerNet !== null ? 'airbnb_expected_payout_amount' : 'airbnb_expected_payout_amount_missing';
    
    // Gross guest paid = airbnb_total_paid_amount
    snapshot.grossGuestPaid = parseDecimal(apiReservation.airbnb_total_paid_amount);
    snapshot.grossGuestPaidReason = snapshot.grossGuestPaid !== null ? 'airbnb_total_paid_amount' : 'airbnb_total_paid_amount_missing';
    
    // Channel fees = commission (informational)
    snapshot.channelFees = parseDecimal(apiReservation.channel_commission_amount);
    snapshot.channelFeesReason = snapshot.channelFees !== null ? 'channel_commission_amount' : 'channel_commission_amount_missing';
    
    // Processing fees: Airbnb doesn't typically show separate processing fees
    snapshot.processingFees = null;
    snapshot.processingFeesReason = 'airbnb_no_separate_processing_fee';
    
    // Taxes: informational (may be channel-managed)
    snapshot.taxes = null;
    snapshot.taxesReason = 'airbnb_taxes_channel_managed';
    
  } else if (channel === 'vrbo' || channel === 'booking.com' || channel === 'bookingcom') {
    // VRBO/Booking.com (Stripe): ownerNet = payout_amount (after Stripe fees)
    snapshot.ownerNet = parseDecimal(apiReservation.payout_amount);
    snapshot.ownerNetReason = snapshot.ownerNet !== null ? 'payout_amount' : 'payout_amount_missing';
    
    // Processing fees = PaymentServiceProcessingFee or BookingcomPaymentProcessingFee
    snapshot.processingFees = parseDecimal(apiReservation.payment_service_processing_fee) || 
                               parseDecimal(apiReservation.bookingcom_payment_processing_fee);
    snapshot.processingFeesReason = snapshot.processingFees !== null 
      ? (apiReservation.payment_service_processing_fee ? 'payment_service_processing_fee' : 'bookingcom_payment_processing_fee')
      : 'processing_fee_missing';
    
    // Channel fees = channel_fee (informational)
    snapshot.channelFees = parseDecimal(apiReservation.channel_fee);
    snapshot.channelFeesReason = snapshot.channelFees !== null ? 'channel_fee' : 'channel_fee_missing';
    
    // Gross guest paid = total_price (if available)
    snapshot.grossGuestPaid = parseDecimal(apiReservation.total_price);
    snapshot.grossGuestPaidReason = snapshot.grossGuestPaid !== null ? 'total_price' : 'total_price_missing';
    
    // Taxes: informational (may be channel-managed)
    snapshot.taxes = null;
    snapshot.taxesReason = 'vrbo_booking_taxes_channel_managed';
    
  } else if (channel === 'direct' || channel === 'website' || channel === 'booking engine') {
    // Direct/Booking Engine: ownerNet = host_payout or payout_amount
    snapshot.ownerNet = parseDecimal(apiReservation.host_payout) || parseDecimal(apiReservation.payout_amount);
    snapshot.ownerNetReason = snapshot.ownerNet !== null 
      ? (apiReservation.host_payout ? 'host_payout' : 'payout_amount')
      : 'host_payout_payout_amount_missing';
    
    // Processing fees = PaymentServiceProcessingFee (Stripe)
    snapshot.processingFees = parseDecimal(apiReservation.payment_service_processing_fee);
    snapshot.processingFeesReason = snapshot.processingFees !== null ? 'payment_service_processing_fee' : 'processing_fee_missing';
    
    // Channel fees: typically none for direct bookings
    snapshot.channelFees = null;
    snapshot.channelFeesReason = 'direct_booking_no_channel_fee';
    
    // Gross guest paid = total_price
    snapshot.grossGuestPaid = parseDecimal(apiReservation.total_price);
    snapshot.grossGuestPaidReason = snapshot.grossGuestPaid !== null ? 'total_price' : 'total_price_missing';
    
    // Taxes: informational
    snapshot.taxes = null;
    snapshot.taxesReason = 'direct_booking_taxes_managed_separately';
    
  } else {
    // Unknown channel or iCal: fallback to available fields
    snapshot.ownerNet = parseDecimal(apiReservation.host_payout) || 
                       parseDecimal(apiReservation.payout_amount) ||
                       parseDecimal(apiReservation.airbnb_expected_payout_amount);
    snapshot.ownerNetReason = snapshot.ownerNet !== null 
      ? (apiReservation.host_payout ? 'host_payout' : 
         apiReservation.payout_amount ? 'payout_amount' : 
         'airbnb_expected_payout_amount')
      : 'no_payout_field_available';
    
    snapshot.processingFees = parseDecimal(apiReservation.payment_service_processing_fee) ||
                              parseDecimal(apiReservation.bookingcom_payment_processing_fee);
    snapshot.processingFeesReason = snapshot.processingFees !== null 
      ? 'payment_service_processing_fee_or_bookingcom'
      : 'processing_fee_missing';
    
    snapshot.channelFees = parseDecimal(apiReservation.channel_fee) ||
                          parseDecimal(apiReservation.channel_commission_amount);
    snapshot.channelFeesReason = snapshot.channelFees !== null 
      ? (apiReservation.channel_fee ? 'channel_fee' : 'channel_commission_amount')
      : 'channel_fee_missing';
    
    snapshot.grossGuestPaid = parseDecimal(apiReservation.total_price) ||
                             parseDecimal(apiReservation.airbnb_total_paid_amount);
    snapshot.grossGuestPaidReason = snapshot.grossGuestPaid !== null 
      ? (apiReservation.total_price ? 'total_price' : 'airbnb_total_paid_amount')
      : 'gross_guest_paid_missing';
    
    snapshot.taxes = null;
    snapshot.taxesReason = 'unknown_channel_taxes_unknown';
  }

  // Owner Net = Owner Revenue (post-deductions); single source of truth from canonical only
  if (canonicalOwnerRevenueNum != null && !isNaN(canonicalOwnerRevenueNum)) {
    snapshot.ownerNet = canonicalOwnerRevenueNum;
    snapshot.ownerNetReason = 'canonical_financial_summary.owner_revenue.amount';
  }
  // When canonical owner_revenue missing: ownerNet stays null (display N/A); no fallback computation

  return snapshot;
}

/**
 * Compute rollup eligibility for a reservation
 * @param {Object} apiReservation - Raw reservation from API
 * @returns {Object} - Rollup eligibility info
 */
function computeRollupEligibility(apiReservation) {
  const status = apiReservation.status || '';
  const statusBucket = getStatusBucket(status);
  
  // Inquiry and Cancelled are excluded from rollups
  const isExcluded = isInquiryStatus(status) || isCancelledStatus(status);
  
  return {
    rollupEligible: !isExcluded,
    rollupExclusionReason: isExcluded 
      ? (isInquiryStatus(status) ? 'INQUIRY' : 'CANCELLED')
      : null,
    statusClass: isExcluded ? 'excluded' : 'eligible',
  };
}

/**
 * Map a single reservation from API format to UI format
 * @param {Object} apiReservation - Raw reservation from API
 * @returns {Object} - Mapped reservation for UI
 */
export function mapReservationToUI(apiReservation) {
  if (!apiReservation) {
    return null;
  }

  // Compute financial snapshot (for ALL statuses)
  const financials = computeFinancialSnapshot(apiReservation);
  
  // Compute rollup eligibility
  const rollupInfo = computeRollupEligibility(apiReservation);

  // CRITICAL: Preserve ALL finance fields from API (for validation mode)
  // Explicitly preserve finance standard fields and extracted fields
  const financeFields = {
    // Finance Standard Fields
    base_rate: apiReservation.base_rate,
    cleaning_fee_value: apiReservation.cleaning_fee_value,
    guest_channel_fee: apiReservation.guest_channel_fee,
    host_channel_fee: apiReservation.host_channel_fee,
    lodging_tax: apiReservation.lodging_tax,
    vat: apiReservation.vat,
    finance_total: apiReservation.finance_total,
    finance_damage_deposit: apiReservation.finance_damage_deposit,
    finance_currency: apiReservation.finance_currency,
    manually_set_fields: apiReservation.manually_set_fields,
    
    // Extracted Table Fields
    airbnb_expected_payout_amount: apiReservation.airbnb_expected_payout_amount,
    airbnb_total_paid_amount: apiReservation.airbnb_total_paid_amount,
    channel_commission_amount: apiReservation.channel_commission_amount,
    host_payout: apiReservation.host_payout,
    channel_fee: apiReservation.channel_fee,
    payout_amount: apiReservation.payout_amount,
    payment_service_processing_fee: apiReservation.payment_service_processing_fee,
    bookingcom_payment_processing_fee: apiReservation.bookingcom_payment_processing_fee,
    host_fee: apiReservation.host_fee,
    platform_fee: apiReservation.platform_fee,
    guest_service_fee: apiReservation.guest_service_fee,
    host_service_fee: apiReservation.host_service_fee,
    security_deposit_fee: apiReservation.security_deposit_fee,
    booking_fee: apiReservation.booking_fee,
    early_checkin_fee: apiReservation.early_checkin_fee,
    monthly_discount: apiReservation.monthly_discount,
    discount_amount: apiReservation.discount_amount,
    coupon_discount: apiReservation.coupon_discount,
    airbnb_transient_occupancy_tax_paid_amount: apiReservation.airbnb_transient_occupancy_tax_paid_amount,
    airbnb_listing_base_price: apiReservation.airbnb_listing_base_price,
    airbnb_listing_cleaning_fee: apiReservation.airbnb_listing_cleaning_fee,
    airbnb_listing_security_price: apiReservation.airbnb_listing_security_price,
    airbnb_listing_cancellation_host_fee: apiReservation.airbnb_listing_cancellation_host_fee,
    airbnb_listing_cancellation_payout: apiReservation.airbnb_listing_cancellation_payout,
    airbnb_occupancy_tax_amount_paid_to_host: apiReservation.airbnb_occupancy_tax_amount_paid_to_host,
    cleaning_fee_paid: apiReservation.cleaning_fee_paid,
    tax_amount_paid: apiReservation.tax_amount_paid,
    damage_deposit: apiReservation.damage_deposit,
    additional_guests_fee: apiReservation.additional_guests_fee,
    pet_fee: apiReservation.pet_fee,
    late_checkout_fee: apiReservation.late_checkout_fee,
    extra_night_price: apiReservation.extra_night_price,
    refund_amount: apiReservation.refund_amount,
    refunded_amount: apiReservation.refunded_amount,
    payment_amount: apiReservation.payment_amount,
    paid_amount: apiReservation.paid_amount,
    balance: apiReservation.balance,
    balance_due: apiReservation.balance_due,
    commission_amount: apiReservation.commission_amount,
    guest_original_price: apiReservation.guest_original_price,
    reservation_fees: apiReservation.reservation_fees,
    finance_field: apiReservation.finance_field,
  };

  return {
    // Preserve all original fields
    ...apiReservation,
    
    // Explicitly preserve finance fields (validation mode requirement)
    ...financeFields,
    
    // Normalize date fields (API uses checkin_date/checkout_date, UI may expect check_in_date/check_out_date)
    check_in_date: apiReservation.checkin_date || apiReservation.check_in_date || null,
    check_out_date: apiReservation.checkout_date || apiReservation.check_out_date || null,
    checkin_date: apiReservation.checkin_date || null,
    checkout_date: apiReservation.checkout_date || null,
    
    // Normalize numeric fields (ensure numbers, not strings)
    nights: typeof apiReservation.nights === 'number' 
      ? apiReservation.nights 
      : (apiReservation.nights ? Number(apiReservation.nights) : 0),
    
    guest_count: typeof apiReservation.guest_count === 'number'
      ? apiReservation.guest_count
      : (apiReservation.guest_count ? Number(apiReservation.guest_count) : 0),
    
    // Normalize revenue/price fields (backward compatibility)
    revenue: apiReservation.total_price 
      ? (typeof apiReservation.total_price === 'number' 
          ? apiReservation.total_price 
          : parseFloat(apiReservation.total_price) || 0)
      : 0,
    total_price: apiReservation.total_price
      ? (typeof apiReservation.total_price === 'number'
          ? apiReservation.total_price
          : parseFloat(apiReservation.total_price) || 0)
      : 0,
    
    // Financial snapshot (computed for ALL statuses)
    financials: financials,
    
    // Rollup eligibility gate
    rollupEligible: rollupInfo.rollupEligible,
    rollupExclusionReason: rollupInfo.rollupExclusionReason,
    statusClass: rollupInfo.statusClass,
    
    // Guest name - preserve from API (backend now includes via JOIN to extracted table)
    // Fallback to 'Guest' only if not available
    guest_name: apiReservation.guest_name || 'Guest',
    email: apiReservation.email || null,
    phone: apiReservation.phone || null,
    note: apiReservation.note || apiReservation.notes || null,
    hostaway_reservation_id: apiReservation.hostaway_reservation_id || apiReservation.reservation_id || null,
    pending_sync: apiReservation.pending_sync || undefined,
    owner_portal_override: apiReservation.owner_portal_override || undefined,
    owner_portal_override_action: apiReservation.owner_portal_override_action || undefined,
    
    // Preserve status and other fields as-is
    status: apiReservation.status || null,
    channel: apiReservation.channel || null,
    property_name: apiReservation.property_name || null,
    reservation_id: apiReservation.reservation_id || null,
    listing_id: apiReservation.listing_id || null,
    currency: apiReservation.currency || 'USD',
    booking_date: apiReservation.booking_date || null,
    lead_time_days: apiReservation.lead_time_days || null,
  };
}

/**
 * Map an array of reservations from API format to UI format
 * @param {Array} apiReservations - Array of raw reservations from API
 * @returns {Array} - Array of mapped reservations for UI
 */
export function mapReservationsToUI(apiReservations) {
  if (!Array.isArray(apiReservations)) {
    return [];
  }
  
  return apiReservations
    .map(mapReservationToUI)
    .filter(reservation => reservation !== null);
}

/**
 * Convert reservation to calendar event format (for CalendarPage)
 * @param {Object} reservation - Mapped reservation (from mapReservationToUI)
 * @returns {Object|null} - Calendar event object or null if invalid
 */
export function reservationToCalendarEvent(reservation) {
  if (!reservation || !reservation.check_in_date || !reservation.check_out_date) {
    return null;
  }
  
  return {
    reservation_id: reservation.reservation_id,
    hostaway_reservation_id: reservation.hostaway_reservation_id || reservation.reservation_id,
    listing_id: reservation.listing_id,
    check_in_date: reservation.check_in_date || reservation.checkin_date,
    check_out_date: reservation.check_out_date || reservation.checkout_date,
    checkin_date: reservation.checkin_date || reservation.check_in_date,
    checkout_date: reservation.checkout_date || reservation.check_out_date,
    status: reservation.status,
    channel: reservation.channel,
    channel_code: reservation.channel || reservation.channel_code,
    property_name: reservation.property_name,
    nights: reservation.nights,
    guest_count: reservation.guest_count,
    guest_name: reservation.guest_name,
    email: reservation.email || null,
    phone: reservation.phone || null,
    note: reservation.note || reservation.notes || null,
    total_price: reservation.total_price,
    currency: reservation.currency,
    pending_sync: reservation.pending_sync || undefined,
    owner_portal_override: reservation.owner_portal_override || undefined,
    owner_portal_override_action: reservation.owner_portal_override_action || undefined,
    // Preserve canonical_financial_summary for master channel metadata
    canonical_financial_summary: reservation.canonical_financial_summary || null,
  };
}
